import UI5Element from "@ui5/webcomponents-base/dist/UI5Element.js";
import customElement from "@ui5/webcomponents-base/dist/decorators/customElement.js";
import property from "@ui5/webcomponents-base/dist/decorators/property.js";
import slot from "@ui5/webcomponents-base/dist/decorators/slot.js";
import event from "@ui5/webcomponents-base/dist/decorators/event.js";
import litRender from "@ui5/webcomponents-base/dist/renderer/LitRenderer.js";
import {
  isUp,
  isDown,
  isHome,
  isEnd,
  isEnter,
  isSpace,
} from "@ui5/webcomponents-base/dist/Keys.js";
import { getI18nBundle } from "@ui5/webcomponents-base/dist/i18nBundle.js";
import type I18nBundle from "@ui5/webcomponents-base/dist/i18nBundle.js";
import getEffectiveScrollbarStyle from "@ui5/webcomponents-base/dist/util/getEffectiveScrollbarStyle.js";
import UDExListItem from "./ListItem.js";
import { ListStyleType, ListMode, LetterPressedHandlerObj } from "./types/List.js";
import UDExListGroupHeader from "./ListGroupHeader.js";
import UDExListGroupHeaderBase from "./ListGroupHeaderBase.js";
import {
  ARIA_LABEL_LIST_MULTISELECTABLE,
  ARIA_LABEL_LIST_SELECTABLE,
} from "./generated/i18n/i18n-defaults.js";
import ListTemplate from "./generated/templates/ListTemplate.lit.js";

// Styles
import ListCss from "./generated/themes/List.css.js";
import isLetterKey from "./utils/isLetterKeyPressed.js";
import listItemLabelStartsWithKeyFocus from "./utils/listItemLabelStartsWithKeyFocus.js";

export type UDExListItemBase = UDExListItem & UDExListGroupHeader;
/**
 * @class
 *
 * <h3 class="comment-api-title">Overview</h3>
 *\e
 *
 * <h3>Usage</h3>
 *
 * For the <code>udex-list</code>
 * <h3>ES6 Module Import</h3>
 *
 * <code>import "@udex/web-components/dist/List.js";</code>
 *
 * @constructor
 * @author SAP SE
 * @extends UI5Element
 * @tagname udex-list
 * @public
 */
@customElement({
  tag: "udex-list",
  renderer: litRender,
  styles: [ListCss, getEffectiveScrollbarStyle()],
  template: ListTemplate,
  dependencies: [],
})

@event("selection-change", {
  detail: {
    listItems: { type: Array },
    selectedItems: { type: Array },
    item: { type: HTMLElement },
  },
})

class UDExList extends UI5Element {
  /**
   * Defines the accessible role of the component.
   *
   * @default "list"
   * @public
   */
  @property({ defaultValue: "list" })
    accessibleRole!: string;

  /**
   * Defines the accessible name of the component.
   *
   * @default ""
   * @public
   */
  @property()
    accessibleName!: string;

  /**
   * Defines the mode of the component.
   *
   * @default "SingleSelect"
   * @public
   */
  @property({ type: ListMode, defaultValue: ListMode.SingleSelect })
    mode!: `${ListMode}`;

  /**
   * Defines the type of the item.
   *
   * @defaultvalue "None"
   * @public
   */
  @property({ type: ListStyleType, defaultValue: ListStyleType.None })
    type!: `${ListStyleType}`;

  /**
   * Allows control over the height.
   *
   * @public
   */
  @property({ type: String })
    height?: string;

  /**
   * Defines the controlled of the component.
   *
   * @default false
   * @public
   */
  @property({ type: Boolean })
    controlled?: boolean;

  /**
   * Defines the headerText, displayed in the top of the list.
   *
   * @public
   */
  @property({ type: String })
    headerText?: string;

  /**
   * Defines the name of the UI5 Icon, that will be displayed for each item of the list.
   *
   * @defaultvalue ""
   * @private
   */
  @property({ type: String, defaultValue: "" })
    itemIcon?: string;

  /**
   * Slot for list items.
   *
   * @slot items
   * @public
   */
  @slot({ type: HTMLElement, "default": true })
    items!: Array<UDExListItemBase>;

  static i18nBundle: I18nBundle;

  _pressedKey!: string;
  _pressedCount!: number;
  _lastFoundPosition!: number;
  _focusableListItems!: Array<UDExListItemBase>;
  _letterKeyHandlerObj!: LetterPressedHandlerObj;

  static async onDefine(): Promise<void> {
    UDExList.i18nBundle = await getI18nBundle("@udex/web-components");
  }

  resetFocusLetterHandlers(): void {
    this.setPressedCount(0);
    this.setPressedKey("");
    this.setLastFoundPosition(0);
  }

  onBeforeRendering(): void {
    this.prepareListItemsMode();
    this.resetFocusLetterHandlers();
    this._focusableListItems = this.getItemsWithoutHeader();
    this._letterKeyHandlerObj = this.getLetterKeyObjHandler();
  }

  handleItemPress(e: CustomEvent): void {
    const clickedItem: UDExListItemBase = e.detail.item;
    this.onItemPress(clickedItem);
  }

  handleKeyDown(e: KeyboardEvent): void {
    if (isUp(e)) {
      this.handleUpArrow(e);
    }
    if (isDown(e)) {
      this.handleDownArrow(e);
    }
    if (isHome(e)) {
      this.handleHomeKey(e);
    }
    if (isEnd(e)) {
      this.handleEndKey(e);
    }
    if (isEnter(e) || isSpace(e)) {
      this.handleEnterOrSpace(e);
    }
    if (isLetterKey(e.key)) {
      this.handleLetterKey(e);
    }
  }

  handleEnterOrSpace(e: KeyboardEvent): void {
    e.preventDefault();
    const clickedItem = e.target as UDExListItemBase;
    this.onItemPress(clickedItem);
  }

  handleUpArrow(e: KeyboardEvent): void {
    e.preventDefault();
    const items = this.getKeyboardNavigationItems();
    const currentSelectedIndex = this.getCurrentIndex(items, e.target as UDExListItemBase);
    const lastItemIndex = items.length - 1;
    const previousSelectIndex = currentSelectedIndex - 1 < 0 ? lastItemIndex : currentSelectedIndex - 1;
    const isHeaderItem = items[previousSelectIndex].isGroup;
    const nonInteractiveHeader = items[previousSelectIndex].nonInteractive;

    if (nonInteractiveHeader && previousSelectIndex === 0) {
      this.unsetTabIndexByIndex(items, currentSelectedIndex);
      this.setTabIndexByIndex(items, lastItemIndex);
      items[lastItemIndex].focus();
      return;
    }

    if (nonInteractiveHeader) {
      this.unsetTabIndexByIndex(items, currentSelectedIndex);
      this.setTabIndexByIndex(items, previousSelectIndex - 1);
      items[previousSelectIndex - 1].focus();
      return;
    }

    if (isHeaderItem) {
      this.unsetTabIndexByIndex(items, currentSelectedIndex);
      this.setTabIndexByIndex(items, previousSelectIndex);
      this.focusHeader(items[previousSelectIndex]);
      return;
    }

    if (this.modeRadioGroup) {
      items[currentSelectedIndex].selected = false;
      items[previousSelectIndex].selected = true;
    }

    this.unsetTabIndexByIndex(items, currentSelectedIndex);
    this.setTabIndexByIndex(items, previousSelectIndex);
    items[previousSelectIndex].focus();
  }

  handleDownArrow(e: KeyboardEvent): void {
    e.preventDefault();
    const items = this.getKeyboardNavigationItems();
    const currentSelectedIndex = this.getCurrentIndex(items, e.target as UDExListItemBase);
    const nextSelectIndex = currentSelectedIndex + 1 > items.length - 1 ? 0 : currentSelectedIndex + 1;
    const isHeaderItem = items[nextSelectIndex].isGroup;
    const nonInteractiveHeader = items[nextSelectIndex].nonInteractive;

    if (nonInteractiveHeader) {
      this.unsetTabIndexByIndex(items, currentSelectedIndex);
      this.setTabIndexByIndex(items, nextSelectIndex + 1);
      items[nextSelectIndex + 1].focus();
      return;
    }

    if (isHeaderItem) {
      this.unsetTabIndexByIndex(items, currentSelectedIndex);
      this.setTabIndexByIndex(items, nextSelectIndex);
      this.focusHeader(items[nextSelectIndex]);
      return;
    }

    if (this.modeRadioGroup) {
      items[currentSelectedIndex].selected = false;
      items[nextSelectIndex].selected = true;
    }

    this.unsetTabIndexByIndex(items, currentSelectedIndex);
    this.setTabIndexByIndex(items, nextSelectIndex);
    items[nextSelectIndex].focus();
  }

  handleHomeKey(e: KeyboardEvent): void {
    e.preventDefault();
    const items = this.getKeyboardNavigationItems();
    const currentSelectedIndex = this.getCurrentIndex(items, e.target as UDExListItemBase);
    const firstItem = items[0];
    const isHeaderItem = firstItem.isGroup;
    const nonInteractiveItem = firstItem.nonInteractive;

    if (nonInteractiveItem) {
      this.unsetTabIndexByIndex(items, currentSelectedIndex);
      this.setTabIndexByIndex(items, 1);
      items[1].focus();
      return;
    }

    if (isHeaderItem) {
      this.unsetTabIndexByIndex(items, currentSelectedIndex);
      this.setTabIndexByIndex(items, 0);
      this.focusHeader(items[0]);
      return;
    }

    if (this.modeRadioGroup) {
      items[currentSelectedIndex].selected = false;
      items[0].selected = true;
    }

    this.unsetTabIndexByIndex(items, currentSelectedIndex);
    this.setTabIndexByIndex(items, 0);
    items[0].focus();
  }

  handleEndKey(e: KeyboardEvent): void {
    e.preventDefault();
    const items = this.getKeyboardNavigationItems();
    const currentSelectedIndex = this.getCurrentIndex(items, e.target as UDExListItemBase);

    if (this.modeRadioGroup) {
      items[currentSelectedIndex].selected = false;
      items[items.length - 1].selected = true;
    }

    this.unsetTabIndexByIndex(items, currentSelectedIndex);
    this.setTabIndexByIndex(items, items.length - 1);
    items[items.length - 1].focus();
  }

  handleLetterKey(e: KeyboardEvent): void {
    const letterKey = e.key;
    e.stopPropagation();
    e.preventDefault();
    listItemLabelStartsWithKeyFocus(letterKey, this._focusableListItems, this._letterKeyHandlerObj);
  }

  onItemPress(clickedItem: UDExListItemBase): void {
    if (this.modeSingleSelect && !this.controlled) {
      this.revertSelectedItems(this.getSelectedItems());
      clickedItem.selected = true;
    }

    if (this.modeRadioGroup && !this.controlled) {
      const groupHeader = clickedItem.parentElement as UDExListGroupHeaderBase;
      const groupItems = groupHeader.getItems() as Array<UDExListItemBase>;
      this.revertSelectedItems(groupItems);
      clickedItem.selected = true;
    }

    this.resetTabIndexes();
    clickedItem._tabIndex = "0";

    this.fireEvent("selection-change", {
      listItems: this.getItemsFromList(),
      selectedItems: this.getSelectedItems(),
      item: clickedItem,
    });
  }

  revertSelectedItems(items: Array<UDExListItemBase>): void {
    items.forEach((item: UDExListItemBase) => {
      if (!item.disabled) {
        item.selected = false;
      }
    });
  }

  focusSelectedItem(): void {
    const selectedItemIndex = this.items.findIndex(item => item.selected);
    this.resetTabIndexes();
    if (selectedItemIndex !== -1 && this.modeSingleSelect) {
      this.setTabIndexByIndex(this.items, selectedItemIndex);
    }
  }

  focusHeader(item: UDExListItemBase): void {
    const header = item.shadowRoot?.querySelector(".udex-list__group-header") as HTMLElement;
    header.focus();
  }

  unsetTabIndexByIndex(items: UDExListItemBase[], index: number): void {
    items[index]._tabIndex = "-1";
  }

  setTabIndexByIndex(items: UDExListItemBase[], index: number): void {
    items[index]._tabIndex = "0";
  }

  setPressedKey(value:string): void {
    this._pressedKey = value;
  }

  getPressedKey(): string {
    return this._pressedKey;
  }

  setPressedCount(value:number): void {
    this._pressedCount = value;
  }

  getPressedCount() : number {
    return this._pressedCount;
  }

  setLastFoundPosition(value:number): void {
    this._lastFoundPosition = value;
  }

  getLastFoundPosition(): number {
    return this._lastFoundPosition;
  }

  getLetterKeyObjHandler(): LetterPressedHandlerObj {
    return {
      getPressedKey: () => this.getPressedKey(),
      setPressedKey: (arg: string) => this.setPressedKey(arg),
      getPressedCount: () => this.getPressedCount(),
      setPressedCount: (arg: number) => this.setPressedCount(arg),
      getLastFoundPosition: () => this.getLastFoundPosition(),
      setLastFoundPosition: (arg: number) => this.setLastFoundPosition(arg),
    } as LetterPressedHandlerObj;
  }

  getItems(): Array<UDExListItemBase> {
    return this.getSlottedNodes<UDExListItemBase>("items");
  }

  getSelectedItems(): Array<UDExListItemBase> {
    return this.getItemsFromList().filter(item => item.selected);
  }

  getCurrentIndex(items: Array<UDExListItemBase>, item: UDExListItemBase): number {
    return items.indexOf(item);
  }

  getItemsWithoutHeader(): Array<UDExListItemBase> {
    return this.getItemsFromList().filter(item => !item.isGroup);
  }

  getKeyboardNavigationItems(): Array<UDExListItemBase> {
    const items = this.modeMultiSelect ? this.getItemsFromList() : this.getItemsWithoutHeader();
    return items.filter(item => !item.disabled);
  }

  getItemsFromList(): Array<UDExListItemBase> {
    const items: UDExListItemBase[] = [];
    const slottedItems = this.getItems();

    slottedItems.forEach(item => {
      const childItems = item.isGroup ? item.children : [];
      items.push(item, ...childItems as UDExListItemBase[]);
    });

    return items;
  }

  prepareListItemsMode(): void {
    this.getItemsFromList().forEach((item, index) => {
      item.mode = this.mode;
      item.controlled = this.controlled;
      item.type = this.type;
      item._icon = this.itemIcon;
      item._tabIndex = this.getTabIndex(index, item);
    });
  }

  getTabIndex(index: number, item: UDExListItemBase): string {
    if ((item.modeSingleSelect || this.modeRadioGroup) && this.isGroupList && index === 1) {
      return "0";
    }

    if (this.modeSelected && index === 0) {
      return "0";
    }

    return "-1";
  }

  resetTabIndexes(): void {
    this.getItemsFromList().forEach(item => {
      item._tabIndex = "-1";
    });
  }

  get isGroupList(): boolean {
    return this.getItemsFromList().some(item => item.isGroup);
  }

  get ariaLabelModeText(): string {
    if (this.modeSingleSelect || this.modeRadioGroup) {
      return UDExList.i18nBundle.getText(ARIA_LABEL_LIST_SELECTABLE);
    }
    return UDExList.i18nBundle.getText(ARIA_LABEL_LIST_MULTISELECTABLE);
  }

  get ariaLabelledBy(): string {
    return `${this._id}-mode-label`;
  }

  get modeRadioGroup(): boolean {
    return this.mode === ListMode.RadioGroup;
  }

  get modeMultiSelect(): boolean {
    return this.mode === ListMode.MultiSelect;
  }

  get modeSingleSelect(): boolean {
    return this.mode === ListMode.SingleSelect;
  }

  get modeSingleContent(): boolean {
    return this.mode === ListMode.SingleContent;
  }

  get modeSelected(): boolean {
    return [ListMode.SingleSelect, ListMode.MultiSelect, ListMode.RadioGroup].includes(this.mode as ListMode);
  }

  get holderHeight(): string {
    return this.height ? this.height : "auto";
  }
}

UDExList.define();

export default UDExList;
